import {
  Component,
  input,
  output,
  signal,
  computed,
  ElementRef,
  viewChild,
  HostListener,
  effect,
} from '@angular/core';
import { FundTypes, NumberFormatConfig } from '@gs-2.0/core';
import { DropdownItem } from '../dropdown/dropdown.component';
import { calculateDropdownPosition } from '../../utils/dropdown-position.util';

// Re-export utility functions for external use
export { transformFundData, transformFundsArray } from './fund-switcher.utils';
export type { RawFund } from './fund-switcher.utils';

// Minimum gap kept between the dropdown panel and the edge of the viewport.
const DROPDOWN_VIEWPORT_MARGIN = 12;

/**
 * Represents a fund with its display information
 */
export interface FundDisplay {
  /**
   * Unique identifier for the fund
   */
  id: string;
  /**
   * Display name of the fund
   */
  name: string;
  /**
   * Account number for the fund
   */
  accountNumber: string;
  /**
   * Current balance of the fund
   */
  balance: number;
  /**
   * Currency code (e.g., 'USD', 'EUR')
   */
  currency: string;
  /**
   * Optional parent fund ID for hierarchical structure
   */
  parentId?: string;
  /**
   * Nesting level (0 for parent, 1+ for sub-funds)
   */
  level?: number;
  /**
   * Optional custom data associated with the fund
   */
  data?: any;
}

/**
 * A fund switcher component that displays the current fund and allows switching between funds.
 * Based on Figma design with button and dropdown functionality.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-14225
 * Dropdown URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-12158
 * Updated dropdown: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=3125-28219
 *
 * @example
 * Basic usage with pre-transformed data:
 * ```html
 * <app-fund-switcher
 *   [funds]="availableFunds"
 *   [selectedFundId]="currentFundId"
 *   (fundChanged)="onFundChange($event)">
 * </app-fund-switcher>
 * ```
 *
 * @example
 * Usage with raw backend data transformation:
 * ```typescript
 * import { transformFundsArray, RawFund } from '@shared/components/fund-switcher';
 *
 * // Raw data from backend
 * const rawFunds: RawFund[] = [
 *   {
 *     cseg5: "1415",
 *     fundName: "Test Charity",
 *     fundNumber: "990041",
 *     closingBalance: 110597.37,
 *     currencySymbol: "USD",
 *     projects: [
 *       {
 *         cseg5: "1738",
 *         fundName: "Project 1",
 *         fundNumber: "990041CA2",
 *         closingBalance: 4685.45,
 *         currencySymbol: "CAD",
 *         projects: []
 *       }
 *     ]
 *   }
 * ];
 *
 * // Transform for component
 * this.availableFunds = transformFundsArray(rawFunds);
 * ```
 */
@Component({
  selector: 'app-fund-switcher',
  templateUrl: './fund-switcher.component.html',
  styleUrls: ['./fund-switcher.component.scss'],
  standalone: false,
})
export class FundSwitcherComponent {
  /**
   * Array of available funds to switch between
   */
  funds = input<FundDisplay[]>([]);

  /**
   * The ID of the currently selected fund
   */
  selectedFundId = input<string>('');

  /**
   * Placeholder text when no fund is selected
   */
  placeholder = input<string>('Select a fund');

  /**
   * Maximum height for the dropdown before scrolling
   */
  dropdownMaxHeight = input<string>('400px');

  /**
   * Disables the trigger button entirely (no open/keyboard activation) — for callers with an
   * empty `funds` list who want to show a placeholder (e.g. "No funds available") without an
   * interactive-looking control.
   */
  disabled = input<boolean>(false);
  /**
   * When true, the dropdown always opens below the trigger button instead of flipping
   * above it when space below is limited. Use this where the switcher's own trigger sits
   * inside a tall scrollable page (e.g. Settings > Promotional Tools > Donate Now) and an
   * "opens above" panel is more likely to be squeezed against the viewport's top edge than
   * genuinely helpful — see GIV-5884.
   */

  forceDropdownBelow = input<boolean>(false);

  /**
   * Visual variant of the fund switcher
   * - 'default': Light blue background
   * - 'yellow': Yellow background
   */
  variant = input<'default' | 'yellow'>('default');

  /**
   * Number format configuration from global config for currency display.
   */
  numberFormat = input<NumberFormatConfig>({
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  });

  /**
   * Emits when a different fund is selected
   */
  fundChanged = output<FundDisplay>();

  /**
   * Reference to the fund switcher container element
   */
  switcherContainer = viewChild<ElementRef>('switcherContainer');

  /**
   * Reference to the dropdown component for keyboard navigation
   */
  dropdownComponent = viewChild<any>('dropdown');

  /**
   * Whether the dropdown is currently open
   */
  isDropdownOpen = signal<boolean>(false);

  /**
   * The currently selected fund object
   */
  selectedFund = signal<FundDisplay | null>(null);

  /**
   * Whether dropdown should open above the button (for positioning)
   */
  dropdownOpenAbove = signal<boolean>(false);

  /**
   * Inline position/size styles for the dropdown panel, computed in viewport (fixed)
   * coordinates so the panel always renders in full even when the switcher sits inside a
   * scrollable ancestor (e.g. `.settings__content { overflow-y: auto }`) that would otherwise
   * clip an absolutely-positioned panel opening "above" the trigger.
   */
  dropdownStyle = signal<Record<string, string>>({});

  /**
   * Set of expanded fund IDs (for collapsible project lists)
   */
  expandedFunds = signal<Set<string>>(new Set());

  /**
   * Timeout for auto-hiding scrollbar
   */
  private scrollbarTimeout: any = null;

  /**
   * Computed display text for the button
   */
  displayText = computed(() => {
    const fund = this.selectedFund();
    return fund ? fund.name : this.placeholder();
  });

  /**
   * Computed display account number for the button
   */
  displayAccountNumber = computed(() => {
    const fund = this.selectedFund();
    // A charity/project-sourced entry (no real fund/account number) has an empty
    // accountNumber — show nothing rather than a bare "#".
    return fund?.accountNumber ? `#${fund.accountNumber}` : '';
  });

  /**
   * Converts funds to dropdown items
   */
  dropdownItems = computed<DropdownItem[]>(() => {
    return this.funds().map((fund) => ({
      id: fund.id,
      label: fund.name,
      data: fund,
    }));
  });

  protected fundViewLabel = computed<string>(() => {
    const fundType = this.selectedFund()?.data?.fundType as string | undefined;
    switch (fundType) {
      case FundTypes.DAF:
        return 'You are in Foundation Fund view';
      case FundTypes.CHARITY:
        return 'You are in Charity Fund view';
      case FundTypes.PROJECT:
      case FundTypes.PROJECT_CHARITY:
        return 'You are in Project Fund view';
      default:
        return '';
    }
  });

  /**
   * Currently selected dropdown item
   */
  selectedDropdownItem = computed<DropdownItem | null>(() => {
    const fund = this.selectedFund();
    if (!fund) return null;

    return {
      id: fund.id,
      label: fund.name,
      data: fund,
    };
  });

  constructor() {
    // Sync selectedFundId input with selectedFund signal
    effect(() => {
      const fundId = this.selectedFundId();
      const fundsList = this.funds();

      if (fundId && fundsList && fundsList.length > 0) {
        const fund = fundsList.find((f) => f.id === fundId);
        this.selectedFund.set(fund || null);
      } else {
        this.selectedFund.set(null);
      }
    });

    // Initialize expanded funds (all parent funds start expanded)
    effect(() => {
      const fundsList = this.funds();
      const parentFundIds = fundsList
        .filter((f) => !f.level || f.level === 0)
        .filter((f) => this.hasProjects(f))
        .map((f) => f.id);

      this.expandedFunds.set(new Set(parentFundIds));
    });

    // Setup scrollbar auto-hide functionality
    effect((onCleanup) => {
      if (this.isDropdownOpen()) {
        // Wait for DOM to render
        setTimeout(() => {
          const dropdownElement = document.querySelector(
            '.fund-switcher__dropdown-inner'
          ) as HTMLElement;

          if (dropdownElement) {
            const handleScroll = () => {
              // Add scrolling class
              dropdownElement.classList.add('is-scrolling');

              // Clear existing timeout
              if (this.scrollbarTimeout) {
                clearTimeout(this.scrollbarTimeout);
              }

              // Set new timeout to remove class after 300ms
              this.scrollbarTimeout = setTimeout(() => {
                dropdownElement.classList.remove('is-scrolling');
              }, 300);
            };

            dropdownElement.addEventListener('scroll', handleScroll);

            // Cleanup
            onCleanup(() => {
              dropdownElement.removeEventListener('scroll', handleScroll);
              if (this.scrollbarTimeout) {
                clearTimeout(this.scrollbarTimeout);
              }
            });
          }
        }, 0);
      }
    });

    // The dropdown panel is positioned in fixed (viewport) coordinates so it isn't clipped by a
    // scrollable ancestor. Fixed coordinates don't follow that ancestor's scroll, so re-anchor the
    // panel to the trigger on every ancestor scroll/resize instead of leaving a stale, misaligned
    // panel on screen. Capture phase catches scroll events from any scrollable ancestor, not just
    // the window (scroll events on overflow containers don't bubble).
    effect((onCleanup) => {
      if (this.isDropdownOpen()) {
        const handleAncestorScroll = (event: Event) => {
          const dropdownElement = document.querySelector('.fund-switcher__dropdown-inner');
          if (dropdownElement && dropdownElement.contains(event.target as Node)) {
            // The fund list itself scrolling internally isn't a reposition trigger.
            return;
          }
          this.repositionOrCloseDropdown();
        };

        const handleResize = () => this.repositionOrCloseDropdown();

        document.addEventListener('scroll', handleAncestorScroll, true);
        window.addEventListener('resize', handleResize);

        onCleanup(() => {
          document.removeEventListener('scroll', handleAncestorScroll, true);
          window.removeEventListener('resize', handleResize);
        });
      }
    });
  }

  /**
   * Formats a balance value using the number format configuration.
   */
  formatBalance(value: number): string {
    const fmt = this.numberFormat();
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart, fmt.digitGroupingSymbol, fmt.digitGrouping);
    const result = grouped + fmt.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string, groupSymbol: string, groupPattern: string): string {
    const patternParts = groupPattern.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;

    const groupSizes = patternParts.map((p) => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;

    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(
        digits
          .slice(pos, pos + size)
          .reverse()
          .join('')
      );
      pos += size;
      sizeIdx++;
    }

    return groups.reverse().join(groupSymbol);
  }

  /**
   * Toggles the dropdown open/closed
   */
  toggleDropdown(): void {
    if (this.disabled()) return;
    if (this.isDropdownOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Opens the dropdown
   */
  openDropdown(): void {
    this.checkDropdownPosition();
    this.isDropdownOpen.set(true);
  }

  /**
   * Closes the dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  /**
   * Re-anchors the open dropdown to its trigger after an ancestor scroll/resize, since the panel
   * is positioned in fixed (viewport) coordinates and doesn't follow the trigger on its own. Falls
   * back to closing only once the trigger has scrolled fully out of the viewport — at that point
   * there's no sane position left to anchor to.
   */
  private repositionOrCloseDropdown(): void {
    const container = this.switcherContainer();
    const rect = container?.nativeElement.getBoundingClientRect();

    if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) {
      this.closeDropdown();
      return;
    }

    this.checkDropdownPosition();
  }

  /**
   * Handles keyboard events on the trigger button
   */
  onTriggerKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    const dropdown = this.dropdownComponent();

    // Open dropdown on Enter or Space or Arrow keys
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();

      if (!this.isDropdownOpen()) {
        this.openDropdown();
      } else if (dropdown) {
        // Pass arrow keys to dropdown for navigation when already open
        dropdown.onKeyDown(event);
      }
    }

    // Close on Escape
    if (event.key === 'Escape' && this.isDropdownOpen()) {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  /**
   * Handles fund selection from dropdown
   */
  onFundSelected(fund: FundDisplay): void {
    this.selectedFund.set(fund);
    this.fundChanged.emit(fund);
    this.closeDropdown();
  }

  /**
   * Checks if a fund has projects (sub-funds)
   */
  hasProjects(fund: FundDisplay): boolean {
    const fundsList = this.funds();
    return fundsList.some((f) => f.id === fund.id && f.data?.projects?.length > 0);
  }

  /**
   * Checks if a fund is currently expanded
   */
  isExpanded(fundId: string): boolean {
    return this.expandedFunds().has(fundId);
  }

  /**
   * Toggles the expansion state of a fund
   */
  toggleExpansion(event: Event, fundId: string): void {
    event.stopPropagation(); // Prevent fund selection when clicking expand icon

    const expanded = new Set(this.expandedFunds());
    if (expanded.has(fundId)) {
      expanded.delete(fundId);
    } else {
      expanded.add(fundId);
    }
    this.expandedFunds.set(expanded);
  }

  /**
   * Determines if a fund should be visible in the dropdown
   * Parent funds (level 0) are always visible
   * Project funds (level > 0) are only visible if their parent is expanded
   */
  shouldShowFund(fund: FundDisplay): boolean {
    // Parent funds are always visible
    if (!fund.level || fund.level === 0) {
      return true;
    }

    // Projects are only visible if their parent is expanded
    if (fund.parentId) {
      return this.isExpanded(fund.parentId);
    }

    return true;
  }

  /**
   * Checks if dropdown should open above or below based on available space, and computes its
   * position/size in fixed (viewport) coordinates so it renders in full instead of being clipped
   * by a scrollable ancestor.
   */
  private checkDropdownPosition(): void {
    const container = this.switcherContainer();
    const dropdownHeight = parseInt(this.dropdownMaxHeight());
    const shouldOpenAbove =
      !this.forceDropdownBelow() && calculateDropdownPosition(container, dropdownHeight);
    this.dropdownOpenAbove.set(shouldOpenAbove);

    if (!container) {
      this.dropdownStyle.set({});
      return;
    }

    const rect = container.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_VIEWPORT_MARGIN;
    const spaceAbove = rect.top - DROPDOWN_VIEWPORT_MARGIN;
    const gap = 8;

    const style: Record<string, string> = {
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };

    if (shouldOpenAbove) {
      style['bottom'] = `${window.innerHeight - rect.top + gap}px`;
      style['max-height'] = `${Math.max(0, Math.min(dropdownHeight, spaceAbove))}px`;
    } else {
      style['top'] = `${rect.bottom + gap}px`;
      style['max-height'] = `${Math.max(0, Math.min(dropdownHeight, spaceBelow))}px`;
    }

    this.dropdownStyle.set(style);
  }

  /**
   * Handles clicks outside the component to close dropdown
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const container = this.switcherContainer();
    if (!container) return;

    const clickedInside = container.nativeElement.contains(event.target as Node);

    if (!clickedInside && this.isDropdownOpen()) {
      this.closeDropdown();
    }
  }
}
